// client/src/components/ticketing/SendTicketsPanel.jsx
//
// Send a free ticket invite directly to a named person's email (Phase 9),
// claim-first: nothing is a real, checkable-in ticket until the recipient
// clicks the emailed link and claims it (server/routes/ticketingRoutes.js
// t_ticket_invite). Rendered as a tab inside TicketConfig.jsx, sharing its
// AuctionConfig.css theme, same structural shape as PromoCodeManager.jsx.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const EMPTY_INVITE = {
  tier_id: "",
  recipient_name: "",
  recipient_email: "",
  num_tickets: "1",
  message: "",
};

const STATUS_LABEL = {
  pending: "Pending",
  claimed: "Claimed",
  expired: "Expired",
  revoked: "Revoked",
};

export default function SendTicketsPanel({ tenantId, eventId, itemId, tiers }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState(EMPTY_INVITE);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [copiedId, setCopiedId] = useState(null);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/invites`);
      if (data.success) setInvites(data.invites);
    } catch (err) {
      console.error("Error loading ticket invites:", err);
    }
  }, [base]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const activeTiers = tiers.filter((t) => ["NEW", "ACTIVE"].includes(t.status));

  const submitInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    setMessage({ text: "", type: "" });

    const payload = {
      tier_id: Number(form.tier_id),
      recipient_name: form.recipient_name.trim(),
      recipient_email: form.recipient_email.trim(),
      num_tickets: Number(form.num_tickets) || 1,
      message: form.message.trim() || null,
    };

    try {
      const { data } = await axios.post(`${base}/invites`, payload);
      if (data.success) {
        setMessage({ text: `Invite sent to ${payload.recipient_email}`, type: "success" });
        setForm(EMPTY_INVITE);
        loadInvites();
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to send invite", type: "error" });
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (inviteId) => {
    if (!window.confirm("Revoke this invite? The recipient will no longer be able to claim it.")) return;
    try {
      await axios.delete(`${base}/invites/${inviteId}`);
      loadInvites();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to revoke invite", type: "error" });
    }
  };

  const copyClaimLink = async (invite) => {
    const url = `${window.location.origin}/ticket-invite/${invite.claim_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <div>
      <p className="share-link-description" style={{ marginBottom: 12 }}>
        Send a free ticket straight to someone's email. They'll get a claim link — nothing is usable
        until they click it and confirm, so a mistyped address never issues a working ticket.
      </p>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={submitInvite} className="auction-config-form" style={{ padding: 16, borderRadius: 12, marginBottom: 16 }}>
        <div className="form-row">
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="invite_tier">Ticket Tier <span className="required">*</span></label>
            <select
              id="invite_tier"
              value={form.tier_id}
              onChange={(e) => setForm((f) => ({ ...f, tier_id: e.target.value }))}
              required
            >
              <option value="">Select a tier…</option>
              {activeTiers.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="invite_name">Recipient Name <span className="required">*</span></label>
            <input
              id="invite_name"
              value={form.recipient_name}
              onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))}
              placeholder="Jamie Rivera"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="invite_email">Recipient Email <span className="required">*</span></label>
            <input
              id="invite_email"
              type="email"
              value={form.recipient_email}
              onChange={(e) => setForm((f) => ({ ...f, recipient_email: e.target.value }))}
              placeholder="jamie@example.com"
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="invite_num">Number of Tickets</label>
            <input
              id="invite_num"
              type="number"
              min="1"
              value={form.num_tickets}
              onChange={(e) => setForm((f) => ({ ...f, num_tickets: e.target.value }))}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="invite_message">Personal Message (optional)</label>
          <input
            id="invite_message"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            placeholder="Hope you can make it!"
            maxLength={500}
          />
        </div>
        <div className="form-actions" style={{ marginTop: 12, paddingTop: 12 }}>
          <button type="submit" disabled={sending || !form.tier_id} className="save-button">
            {sending ? "Sending…" : "Send Invite"}
          </button>
        </div>
      </form>

      {invites.length === 0 ? (
        <div className="ticket-empty-state">No invites sent yet.</div>
      ) : (
        <ul className="ticket-list">
          {invites.map((invite) => (
            <li key={invite.id} className="ticket-list-item">
              <div>
                <div className="ticket-list-item-title">
                  {invite.recipient_name} <span style={{ opacity: 0.6 }}>({invite.recipient_email})</span>
                </div>
                <div className="ticket-list-item-meta">
                  {invite.tier_name} · {invite.num_tickets} ticket{invite.num_tickets === 1 ? "" : "s"} ·{" "}
                  {STATUS_LABEL[invite.status] || invite.status}
                  {invite.status === "pending" && ` (expires ${new Date(invite.expires_at).toLocaleDateString()})`}
                </div>
              </div>
              <div className="ticket-list-item-actions">
                {invite.status === "pending" && (
                  <>
                    <button onClick={() => copyClaimLink(invite)} className="ticket-item-btn">
                      {copiedId === invite.id ? "Copied!" : "Copy claim link"}
                    </button>
                    <button onClick={() => revokeInvite(invite.id)} className="ticket-item-btn danger">
                      Revoke
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
